import type { SandboxRenderResult } from "../../../components/UIToolkit/Previews/SandboxedHtmlRenderer";
import type { AiPrintoutShift } from "../../../downloads/aiPrintout/contract";
import type { HarnessFailureKind } from "../../../downloads/aiPrintout/harnessProtocol";
import type { CustomPrintoutFailureKind, CustomPrintoutMissingShift, CustomPrintoutRepair } from "./customPrintoutTypes";

/** Mirrors `REPAIR_MAX_MISSING_SHIFTS` in `backend/models/printout_templates.py`; more is refused with a 422. */
export const MAX_MISSING_SHIFTS = 500;

/**
 * What each harness failure tells the designer, or `null` when it isn't the template's fault and a new design
 * can't fix it. Exhaustive on purpose: a new harness kind fails to compile here until someone decides.
 */
const HARNESS_FAILURE_TO_REPAIR_KIND: Record<HarnessFailureKind, CustomPrintoutFailureKind | null> = {
	syntax: "syntax",
	runtime: "runtime",
	timeout: "timeout",
	"bad-output": "bad-output",
	"too-large": "too-large",
	// The browser, the frame or the page around it — never the template — so a new design can't help.
	unsupported: null,
	"not-ready": null,
	navigated: null,
};

/**
 * Sent to the designer, not shown to anyone, so English and fixed. Never the template's own error text: a
 * thrown message can quote whatever the template was printing, a person's name included.
 */
const REPAIR_MESSAGES: Record<CustomPrintoutFailureKind, string> = {
	syntax: "The template has a JavaScript syntax error, so render(data) could not run.",
	runtime: "render(data) threw an error while building the page.",
	timeout: "render(data) did not finish within the 5 second limit.",
	"bad-output": "render(data) did not return an HTML string.",
	"too-large": "The template source or the HTML it returned is over the size limit.",
	incomplete: "Some shifts are not visibly on the page: no element tagged with their data-shift-id shows their name readably.",
	"print-media": "The page uses media or container rules, so what prints could differ from the checked screen render.",
};

/** Why a render can't be printed, in the terms the designer is told about; `null` when it can (or isn't done). */
export type CustomPrintoutRenderFailure = { kind: CustomPrintoutFailureKind; missingShiftIds: string[] };

export const renderFailureOf = (result: SandboxRenderResult): CustomPrintoutRenderFailure | null => {
	if (result.status === "failed") {
		const kind = HARNESS_FAILURE_TO_REPAIR_KIND[result.kind];
		return kind === null ? null : { kind, missingShiftIds: [] };
	}
	if (result.status !== "rendered" || result.report.ok) return null;
	if (result.report.missing.length > 0) return { kind: "incomplete", missingShiftIds: result.report.missing };
	return { kind: "print-media", missingShiftIds: [] };
};

/** Each missing shift by role, job, location and time — the only way the designer ever hears about one. */
const describeMissing = (shift: AiPrintoutShift): CustomPrintoutMissingShift => ({
	role: shift.roleShort,
	job: shift.jobName,
	location: shift.location,
	time: shift.timeLabel,
});

/**
 * The repair the designer is sent. Missing shifts that look the same to it (same role, job, location and time)
 * are one entry — it can't tell them apart anyway — and the list is capped where the server caps it.
 */
export const buildRepair = (failure: CustomPrintoutRenderFailure, shifts: AiPrintoutShift[]): CustomPrintoutRepair => {
	const missingIds = new Set(failure.missingShiftIds);
	const distinct = new Map<string, CustomPrintoutMissingShift>();
	for (const shift of shifts) {
		if (!missingIds.has(shift.id)) continue;
		const described = describeMissing(shift);
		distinct.set(JSON.stringify(described), described);
	}
	return { kind: failure.kind, message: REPAIR_MESSAGES[failure.kind], missing: [...distinct.values()].slice(0, MAX_MISSING_SHIFTS) };
};
